import { useState } from 'react'
import {FileText, Menu, X, ChevronDown} from 'lucide-react'

const NAV_ITEMS = [
  {
    label: 'Conversion Tools',
    href: '#uploader',
    sub: [
      { label: 'PDF to PNG', href: '#pdf-to-png' },
      { label: 'EPUB to PDF', href: '#epub-to-pdf' },
      { label: 'MD to PDF', href: '#md-to-pdf' },
      { label: 'XML to PDF', href: '#xml-to-pdf' },
      { label: 'Pages to PDF', href: '#pages-to-pdf' },
    ],
  },
  {
    label: 'Tech / Dev Utilities',
    href: '#ipynb-to-pdf',
    sub: [
      { label: 'IPYNB to PDF', href: '#ipynb-to-pdf' },
      { label: 'MuPDF Web', href: '#mupdf' },
      { label: 'Make PDF Searchable', href: '#make-searchable' },
      { label: 'PDF to Scanned PDF', href: '#pdf-to-scanned' },
    ],
  },
  {
    label: 'PDF Editor',
    href: '#pdf-editor',
    sub: [
      { label: 'PDF Editor (Mac/Web)', href: '#pdf-editor' },
      { label: 'PDF Annotator', href: '#pdf-editor' },
      { label: 'Page Reorder', href: '#pdf-editor' },
    ],
  },
  {
    label: 'eBooks',
    href: '#epub-to-pdf',
    sub: [
      { label: 'EPUB to PDF', href: '#epub-to-pdf' },
      { label: 'MOBI to PDF', href: '#epub-to-pdf' },
      { label: 'EPUB Viewer', href: '#epub-to-pdf' },
    ],
  },
]

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100" role="banner">
      <nav
        className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <a
          href="/"
          className="flex items-center gap-2 flex-shrink-0"
          aria-label="PdfTide — Home"
        >
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shadow-sm">
            <FileText size={16} className="text-white" />
          </div>
          <span
            className="font-semibold text-gray-900 text-lg tracking-tight"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Pdf<span className="text-indigo-600">Tide</span>
          </span>
        </a>

        {/* Desktop nav */}
        <ul className="hidden md:flex items-center gap-1" role="list">
          {NAV_ITEMS.map((item) => (
            <li
              key={item.label}
              className="relative"
              onMouseEnter={() => setActiveDropdown(item.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <a
                href={item.href}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                aria-haspopup={item.sub ? 'true' : undefined}
                aria-expanded={activeDropdown === item.label ? 'true' : 'false'}
              >
                {item.label}
                {item.sub && <ChevronDown size={13} className={`transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />}
              </a>

              {/* Dropdown */}
              {item.sub && activeDropdown === item.label && (
                <ul
                  className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl border border-gray-100 shadow-xl p-1.5 z-50"
                  role="list"
                >
                  {item.sub.map((sub) => (
                    <li key={sub.label}>
                      <a
                        href={sub.href}
                        className="block px-3 py-2 rounded-lg text-sm text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                      >
                        {sub.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="#roi-calculator"
            className="text-sm font-medium text-gray-500 hover:text-indigo-600 transition-colors px-3 py-2"
          >
            ROI Calculator
          </a>
          <a
            href="#uploader"
            className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 transition-all shadow-sm focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Start Converting →
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <div key={item.label}>
              <a
                href={item.href}
                className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-all"
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </a>
              {item.sub?.map((sub) => (
                <a
                  key={sub.label}
                  href={sub.href}
                  className="block px-6 py-2 text-sm text-gray-500 hover:text-indigo-600 transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  {sub.label}
                </a>
              ))}
            </div>
          ))}
          <a
            href="#uploader"
            className="block mt-3 px-4 py-2.5 rounded-full text-sm font-semibold text-center text-white bg-indigo-600 hover:bg-indigo-500 transition-all"
            onClick={() => setMenuOpen(false)}
          >
            Start Converting →
          </a>
        </div>
      )}
    </header>
  )
}
